// FLAGS: none
// The unreachable-API screen both route guards render when the profile query errors.
import { CloudOff } from "lucide-react";
import { Button } from "@openup/shared/primitives/ui/button";

interface ConnectionErrorProps {
  onRetry: () => void;
  isRetrying: boolean;
}

/**
 * Shown when the profile query fails for a reason that is not a signed-out session — a 5xx, a
 * timeout, a dropped connection.
 *
 * Without it, each guard read that error as "no user" and acted on it: ProtectedRoute bounced the
 * scheduler to /login, which reads as "my session ended", and PublicOnlyRoute rendered a login page
 * whose sign-in calls the same unreachable API. Naming the real cause is the difference between a
 * user waiting a minute and a user re-authenticating for no reason.
 */
const ConnectionError = ({ onRetry, isRetrying }: ConnectionErrorProps) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
    <CloudOff className="h-12 w-12 text-indigo-800" aria-hidden />
    <h1 className="text-2xl font-semibold text-indigo-800">
      Can&apos;t reach OpenUp
    </h1>
    <p className="max-w-md text-sm text-gray-600">
      We couldn&apos;t load your account. This is usually a temporary connection
      problem — you have not been signed out.
    </p>
    <Button onClick={onRetry} disabled={isRetrying}>
      {isRetrying ? "Retrying…" : "Try again"}
    </Button>
  </div>
);

export default ConnectionError;
